// inventory.template.js
import React from "react";
import { Link } from "react-router-dom";
import SpinnerComponent from "../../components/atoms/spinner/spinner.component";

const qtyInputStyle = {
  width: 64,
  height: 34,
  border: "1px solid var(--stroke-1)",
  borderRadius: "var(--radius-md)",
  padding: "0 8px",
  fontFamily: "var(--font-mono)",
  fontWeight: 600,
  textAlign: "center",
  color: "var(--fg-1)",
};

const InventoryTemplate = ({ templateData, templateEvent }) => {
  const {
    loading,
    error,
    searchTerm,
    inventoryItems,
    specFilters,
    selectedSmartFilters,
    categoryId,
    currentPage = 1,
    totalPages = 1,
  } = templateData;

  const {
    handleSearch,
    onSearchChange,
    clearSearch,
    handleFilterChange,
    handleApplyFilter,
    handleResetAll,
    handleRemoveChip,
    handlePageChange,
    handleClickPartNumber,
    handleAddToCart,
  } = templateEvent;

  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    for (let i = startPage; i <= endPage; i++) pages.push(i);
    return pages;
  };

  const specCols = inventoryItems?.result?.[0]?.specifications || [];

  return (
    <div className="ds-container">
      {/* Header + search */}
      <div className="ds-page-head">
        <div className="ds-head-row">
          <div>
            <p className="ds-eyebrow">Inventory</p>
            <h1>{inventoryItems?.result?.[0]?.category_name || "Inventory"}</h1>
          </div>
          <div className="ds-search-field" style={{ maxWidth: 320, flex: "0 0 320px" }}>
            <span className="lead">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" />
              </svg>
            </span>
            <input
              type="text"
              placeholder="Filter within results…"
              value={searchTerm}
              onChange={onSearchChange}
              onKeyPress={(e) => e.key === "Enter" && handleSearch()}
            />
            {searchTerm && (
              <button type="button" className="icon-btn" style={{ width: 30, height: 30 }} onClick={clearSearch} aria-label="Clear">×</button>
            )}
          </div>
        </div>
        <p className="sub">Total count: {inventoryItems?.count || 0}</p>
      </div>

      {/* Smart filters */}
      {(specFilters?.make?.length > 0 || specFilters?.spec_values?.length > 0) && (
        <div className="ds-filter-bar" style={{ marginBottom: 12, alignItems: "flex-start", gap: 12 }}>
          {specFilters?.make?.length > 0 && (
            <div>
              <label className="ds-eyebrow" style={{ display: "block", marginBottom: 6 }}>Manufacturer</label>
              <select
                className="form-select"
                multiple
                size={6}
                value={selectedSmartFilters.filter((f) => f.spec_id === "make").map((f) => f.spec_value)}
                data-specid="make"
                onChange={(e) => handleFilterChange("make", "make", e)}
                style={{ minWidth: 160 }}
              >
                {specFilters.make.map((make, index) => (
                  <option key={index} value={make.make}>{make.make}</option>
                ))}
              </select>
            </div>
          )}
          {specFilters?.spec_values?.map((spec, index) => (
            <div key={index}>
              <label className="ds-eyebrow" style={{ display: "block", marginBottom: 6 }}>{spec.specification_name}</label>
              <select
                className="form-select"
                multiple
                size={6}
                value={selectedSmartFilters
                  .filter((f) => f.spec_id === spec.specification_id.toString())
                  .map((f) => f.spec_value)}
                onChange={(e) => handleFilterChange(spec.specification_id.toString(), spec.specification_name, e)}
                style={{ minWidth: 160 }}
              >
                {spec.values && spec.values.map((valueItem, i) => (
                  <option key={`${spec.specification_id}-${i}`} value={valueItem.value}>
                    {valueItem.value} ({valueItem.count})
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>
      )}

      {/* Applied chips + actions */}
      <div className="ds-row ds-between" style={{ marginBottom: 16 }}>
        <div className="ds-row">
          {selectedSmartFilters?.length > 0 && <span className="ds-muted">Applied:</span>}
          {selectedSmartFilters?.map((filter, index) => (
            <span key={index} className="ds-chip on">
              {filter.spec_value}
              <span className="x" onClick={() => handleRemoveChip(filter.spec_id)} role="button" aria-label="Remove">×</span>
            </span>
          ))}
        </div>
        <div className="ds-row">
          <button className="ds-btn ds-btn-ghost ds-btn-sm" onClick={handleResetAll}>Reset all</button>
          <button className="ds-btn ds-btn-primary ds-btn-sm" onClick={handleApplyFilter}>Apply filter</button>
        </div>
      </div>

      {/* Results */}
      {loading ? (
        <div style={{ textAlign: "center", padding: 48 }}><SpinnerComponent /></div>
      ) : error ? (
        <div className="ds-state"><h3>{error}</h3></div>
      ) : (
        <div className="ds-table-wrap">
          <form onSubmit={(e) => e.preventDefault()}>
            <table className="ds-table">
              <thead>
                <tr>
                  <th style={{ width: 60 }}></th>
                  <th>Part number</th>
                  <th>Description</th>
                  <th>Alternatives</th>
                  <th>Manufacturer</th>
                  <th className="num">Stock</th>
                  <th>Qty</th>
                  <th className="num">Price</th>
                  {specCols.length > 0
                    ? specCols.map((spec, index) => <th key={index}>{spec.specification_name}</th>)
                    : <th>Specifications</th>}
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {inventoryItems?.result?.length > 0 ? (
                  inventoryItems.result.map((item, index) => (
                    <tr key={item.id || index}>
                      <td>
                        <span className="ds-thumb" style={{ width: 44, height: 44, aspectRatio: "auto", borderRadius: 4 }}>
                          {item.image ? <img src={item.image} alt="" /> : null}
                        </span>
                      </td>
                      <td>
                        <span
                          className="mpn"
                          style={{ cursor: "pointer", color: "var(--brand-violet)" }}
                          onClick={() => handleClickPartNumber(item.part_number)}
                        >
                          {item.part_number || "N/A"}
                        </span>
                      </td>
                      <td>{item.description || "N/A"}</td>
                      <td>
                        {item.search_alias?.length > 0
                          ? [...new Set(
                              item.search_alias
                                .filter((a) => a.search_alias && a.search_alias.toLowerCase() !== item.part_number.toLowerCase())
                                .map((a) => a.search_alias)
                            )].map((alias, i) => (
                              <div key={i} className="ds-mono" style={{ fontSize: 12, color: "var(--fg-3)" }}>{alias}</div>
                            ))
                          : "N/A"}
                      </td>
                      <td>{item.make || "N/A"}</td>
                      <td className="num">{item.available_qty || "N/A"}</td>
                      <td>
                        <div className="ds-row" style={{ flexWrap: "nowrap", gap: 6 }}>
                          <input type="text" style={qtyInputStyle} ref={(el) => (item.qtyRef = el)} />
                          <button
                            type="button"
                            className="ds-btn ds-btn-primary ds-btn-sm"
                            onClick={() => {
                              const enteredQty = parseInt(item.qtyRef?.value) || 0;
                              handleAddToCart({ ...item, required_qty: enteredQty });
                            }}
                          >
                            Buy
                          </button>
                        </div>
                      </td>
                      <td style={{ textAlign: "right" }}>
                        {item.price && item.price.length > 0
                          ? item.price.map((priceItem, i) => (
                              <div key={i} className="ds-mono" style={{ fontSize: 12, whiteSpace: "nowrap" }}>
                                <span style={{ color: "var(--fg-3)" }}>{priceItem.slab}</span>
                                <span style={{ color: "var(--fg-1)", fontWeight: 600 }}>: ₹{parseFloat(priceItem.price).toFixed(2)}</span>
                              </div>
                            ))
                          : "0.00"}
                      </td>
                      {specCols.length > 0
                        ? item.specifications?.map((spec, si) => <td key={si}>{spec.specification_value || "N/A"}</td>)
                        : <td>N/A</td>}
                      <td>
                        <Link to={`/product/${item.part_number}`} className="ds-btn ds-btn-secondary ds-btn-sm">View</Link>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={9 + Math.max(specCols.length, 1)} style={{ textAlign: "center", color: "var(--danger)", padding: 32 }}>
                      No inventory items found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </form>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <nav className="ds-pagination">
          <button onClick={() => handlePageChange(1)} disabled={currentPage === 1}>« First</button>
          <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>‹ Prev</button>
          {getPageNumbers().map((page) => (
            <button key={page} className={currentPage === page ? "active" : ""} onClick={() => handlePageChange(page)}>
              {page}
            </button>
          ))}
          <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>Next ›</button>
          <button onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages}>Last »</button>
          <span className="info">Page {currentPage} of {totalPages}</span>
        </nav>
      )}
    </div>
  );
};

export default InventoryTemplate;
